import { useLazyQuery, useQuery } from '@apollo/client';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import { IDropdownOption } from '@fluentui/react';
import {
  Combobox,
  Option,
  OptionGroup,
  Field,
  FluentProvider,
  webLightTheme,
  ProgressBar,
} from '@fluentui/react-components';
import React, { useEffect, useState } from 'react';
import {
  GET_DEFAULT_CURRENCY_CODES_QUERY,
  GET_FOREX_CURRENCY_CODES_QUERY,
} from '../../../utils/statApi/CurrencyApi';
import IDropdownReference from '../../../utils/types/IDropdownReference';
import LoadingErrorMessage from '../errorContent/LoadingErrorMessage';
import { savedDividendCurrency, topSuggestedCurrencyCodes } from './CurrencyCodeDropdown.config';

interface ICurrencyCodeDropdownProps {
  propertyName: string;
  useDefaultCurrenciesForCompanyId?: number;
  handleChange: (newValue: string, valueKey: string) => void;
  defaultValue: string;
  required?: boolean;
  disabled?: boolean;
  errorMessage?: string;
  label?: string | JSX.Element;
  width?: number;
}
const CurrencyCodeDropdown = (props: ICurrencyCodeDropdownProps): JSX.Element => {
  const {
    handleChange,
    defaultValue: savedCurrencyCode,
    propertyName,
    required,
    disabled,
    errorMessage,
    useDefaultCurrenciesForCompanyId,
    label,
    width,
  } = props;
  const isCustomLabel = label && typeof label !== 'string';
  const containerWidth = width || 200;

  const [selectedValue, setSelectedValue] = useState(savedCurrencyCode || '');
  const [getDefaultCurrencies, { loading, data, error }] = useLazyQuery(
    GET_DEFAULT_CURRENCY_CODES_QUERY,
  );

  useEffect(() => {
    if (useDefaultCurrenciesForCompanyId) {
      getDefaultCurrencies({ variables: { companyId: useDefaultCurrenciesForCompanyId } });
    }
  }, []);

  const { data: forexCurrencyData, loading: forexCurrencyLoading } = useQuery(
    GET_FOREX_CURRENCY_CODES_QUERY,
  );

  const defaultCompanyCurrencyCodes = data?.defaultCurrencyCodes || [];

  const allForexCurrencyCodes = forexCurrencyData?.currencyCodes || [];

  const loadingCurrencies: boolean = loading || forexCurrencyLoading;

  /**
   * Maps DropdownReference array to DropdownOption array
   */
  const mapToDropdownOptions = (currCodes: IDropdownReference[]): IDropdownOption[] => {
    const result = currCodes
      ? currCodes.map((currencyCode) => {
          return { key: currencyCode.id, text: currencyCode.fieldName };
        })
      : null;
    return result;
  };

  /**
   * Returns
   * 1) current saved currency code (if any),
   * 2) any currency codes saved in the Company's record (e.g. Management Currency, Statutory Currency), and
   * 3) the top suggested currencies in the Stat tool
   */
  const suggestedCurrencyCodes = (): IDropdownOption[] => {
    const companyCurrencyCodes = defaultCompanyCurrencyCodes
      ? mapToDropdownOptions(defaultCompanyCurrencyCodes).concat(topSuggestedCurrencyCodes)
      : topSuggestedCurrencyCodes;

    return savedCurrencyCode
      ? savedDividendCurrency(savedCurrencyCode).concat(companyCurrencyCodes)
      : companyCurrencyCodes;
  };

  /**
   * Returns filtered currencyCodes, removing duplicate currency codes
   */
  const filteredCurrencyCodes = (currencyCodes: IDropdownOption[]): IDropdownOption[] => {
    return currencyCodes.reduce(
      (
        uniqueCurrencyCodes: IDropdownOption[],
        currentCurrencyCode: { key: string; text: string },
      ) => {
        const duplicateCurrencyCode = uniqueCurrencyCodes.find(
          (item) => item.key === currentCurrencyCode.key,
        );
        if (!duplicateCurrencyCode) {
          return uniqueCurrencyCodes.concat([currentCurrencyCode]);
        }
        return uniqueCurrencyCodes;
      },
      [],
    );
  };

  const suggestedCurrencyOptions = filteredCurrencyCodes(suggestedCurrencyCodes());
  const suggestedCurrencyKeys = new Set(suggestedCurrencyOptions.map((option) => option.key));
  const allCurrencyOptions = filteredCurrencyCodes(
    mapToDropdownOptions(allForexCurrencyCodes),
  ).filter((option) => !suggestedCurrencyKeys.has(option.key));

  const onChangeCurrencyOptions = (
    event: React.ChangeEvent<HTMLInputElement>,
    optionData: { optionText?: string; value?: string },
  ): void => {
    const value = optionData.optionText || optionData.value;
    if (value) {
      setSelectedValue(value);
      handleChange(value, `${propertyName}`);
    }
  };

  return (
    <FluentProvider theme={webLightTheme}>
      <LoadingErrorMessage error={error} loading={loadingCurrencies} />
      {loadingCurrencies && (
        <Field label="Loading currencies" hint="Fetching currency data from source">
          <ProgressBar />
        </Field>
      )}
      {!loadingCurrencies && allForexCurrencyCodes && (
        <Stack tokens={{ childrenGap: 5 }}>
          <Field
            label={isCustomLabel ? undefined : (label as string) || 'Currency'}
            required={required}
            validationMessage={errorMessage}
            style={{ width: containerWidth }}
          >
            {isCustomLabel && label}
            <Combobox
              value={selectedValue}
              selectedOptions={[selectedValue]}
              onOptionSelect={onChangeCurrencyOptions}
              disabled={disabled}
              style={{ width: containerWidth }}
            >
              <OptionGroup label="Suggested Currency">
                {suggestedCurrencyOptions.map((option) => (
                  <Option key={option.key} value={option.text} text={option.text}>
                    {option.text}
                  </Option>
                ))}
              </OptionGroup>
              <OptionGroup label="All Currency">
                {allCurrencyOptions.map((option) => (
                  <Option key={option.key} value={option.text} text={option.text}>
                    {option.text}
                  </Option>
                ))}
              </OptionGroup>
            </Combobox>
          </Field>
        </Stack>
      )}
    </FluentProvider>
  );
};

export default CurrencyCodeDropdown;
